import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChangeStatusMessage, GetMessagesRequest, GetMessagesResponse } from '@core/definitions/api/chat.definitions';
import { SERVICES, Session } from '@core/models/Session';
import { AdapterService } from '@core/services/adapter.service';
import { SessionService } from '@core/services/session.service';
import { StorageService } from '@core/services/storage.service';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { Attachment, Conversation, Message, ReciveNewMessage, User } from 'src/app/home/chat/chat.definitions';
import { environment } from 'src/environments/environment';
import { ChatGetMessagesUnreadCountAction, ChatReciveCreateNewConversationAction, ChatReciveMessagesHasReadedAction, ChatReciveMessagesHasRecivedAction, ChatReciveNewMessageAction, ChatSetConversationsAction, ChatSetCurrentUserAction, ChatSetLoadingModuleAction } from '../../home/chat/store/chat.actions';
@Injectable({ providedIn: 'root' })
export class ChatService {
  private socket: Socket = null;
  private user: User;
  private url = environment.apiGatewayChat.url;
  constructor(
    private _adapter: AdapterService,
    private _store: Store,
    private _storage: StorageService,
    private sessionService: SessionService,
    private http: HttpClient,
  ) {
    this.sessionService.logout$.subscribe(() => this.disconnect());
  }
  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }
  }
  async getConversations(): Promise<void> {
    try {
      const response = await this.emitPromise<any>('conversations/mobile', {});
      this._store.dispatch(new ChatSetConversationsAction(response));
    } catch (error) {
      console.error('Error while getting conversations:', error);
      throw error;
    }
  }
  createConversation(conversation: Conversation): Promise<Conversation> {
    const conversationDto = this._adapter.transform<Conversation>(conversation, (conv) => ({
      id: conv.id,
      name: conv.name,
      members: conv.members,
      messages: conv.messages,
      isGroup: conv.isGroup,
      lastSend: conv.lastSend,
      selected: false,
    }));
    return this.emitPromise('conversation/create', conversationDto);
  }
  sendMessagesHasRecived(payload: ChangeStatusMessage): Promise<any> {
    return this.emitPromise('messages/hasRecived', payload);
  }
  async sendMessagesHasReaded(payload: ChangeStatusMessage): Promise<void> {
    await this.emitPromise('messages/hasReaded', payload);
  }
  sendMessage(conversation: string, message: Message): Promise<{ conversation: string, message: Message }> {
    const payload = { conversation, message };
    return this.emitPromise('message/new', payload);
  }
  async getMessagesUnreadCount(userName: string) {
    const messagesCount = await this.emitPromise<{ [key: string]: number }>('messages/unreaded/count', { user: userName });
    this._store.dispatch(new ChatGetMessagesUnreadCountAction(messagesCount));
  }
  getMessages(payload: GetMessagesRequest): Promise<GetMessagesResponse> {
    return this.emitPromise('messages', payload);
  }
  connect(session: Session) {
    try {
      if (!this.isModuleEnable(session)) {
        console.warn('El módulo de chat no está habilitado para esta sesión.');
        return;
      }
      const driver = session.driver;
      if (driver && driver.phone) {
        this.user = { user: driver.phone, name: driver.name };
        this._store.dispatch(new ChatSetCurrentUserAction(this.user));
        setTimeout(async () => {
          try {
            if (this.socket && this.socket.connected) {
              await this.getMessagesUnreadCount(this.user.name);
              return;
            }
            const jwt = await this._storage.get('jwt');
            if (this.socket) {
              this.socket.disconnect();
            }
            this.socket = io(environment.apiGatewayChat.url, {
              reconnection: true,
              extraHeaders: { jwt },
            });
            this.socket.on('connect', async () => {
              this.initSocketListeners();
              await this.getConversations();
              await this.getMessagesUnreadCount(this.user.name);
              this._store.dispatch(new ChatSetLoadingModuleAction(false));
            });
          } catch (error) {
            console.error('Error en la conexión del socket o en la carga de datos:', error);
          }
        }, 200);
      } else {
        console.warn('El driver no tiene información válida.');
      }
    } catch (error) {
      console.error('Error en el método connect:', error);
    }
  }
  async uploadImage(body: { filename: string; base64: string }): Promise<any> {
    const jwt = await this._storage.get('jwt');
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'jwt': jwt || 'no-jwt',
      }),
    };
    return await lastValueFrom(this.http.post(`${this.url}/storage/image`, body, httpOptions));
  }
  async uploadFile(form: FormData): Promise<Attachment> {
    const jwt = await this._storage.get('jwt');
    return await lastValueFrom(this.http.post<Attachment>(`${this.url}/storage/file`, form, {
      headers: new HttpHeaders({ 'jwt': jwt }),
    }));
  }
  private isModuleEnable(session: Session) {
    return session.services.some(service => service.name === SERVICES.ASSISTANT_CHAT);
  }
  private initSocketListeners(): void {
    this.socket.on('conversation/created', (conversation: Conversation) => {
      this._store.dispatch(new ChatReciveCreateNewConversationAction(conversation));
    });
    this.socket.on('message/new', (message: ReciveNewMessage) => {
      this._store.dispatch(new ChatReciveNewMessageAction(message));
    });
    this.socket.on('messages/hasRecived', (status: ChangeStatusMessage) => {
      this._store.dispatch(new ChatReciveMessagesHasRecivedAction(status));
    });
    this.socket.on('messages/hasReaded', (status: ChangeStatusMessage) => {
      this._store.dispatch(new ChatReciveMessagesHasReadedAction(status));
    });
  }
  private emitPromise<T>(event: string, payload: any): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (this.socket && this.socket.connected) {
        this.socket.emit(event, payload, (response: any) => {
          if (response.ok) {
            resolve(response.data);
          } else {
            reject(response);
          }
        });
      }
    });
  }
}
